import { userPaths } from 'routes/constants/userPaths';

import { lockOnboardingAfterOtpVerification } from 'utils/otpVerificationShared';

import {
  END_USER_CONNECT_READY_STATES,
  END_USER_ONBOARDING_STATES,
  type EndUserJourneyState,
} from './endUserJourneyState.data';
import {
  canAccessEndUserFullFlows,
  isEndUserAccountConnected,
  isEndUserAccountConnectionError,
} from './endUserRouteAccess';

export type EndUserJourneyRouteReason = 'onboarding' | 'end-user-home' | 'full-flow';

export type EndUserJourneyRouteResolution = {
  path: string;
  reason: EndUserJourneyRouteReason;
};

/** Backend alias after signup OTP creates the Cognito user. */
export const COGNITO_CREATED_STATE = 'cognito-created';

export function normalizeEndUserJourneyState(state: string | null | undefined): string | null {
  const normalized = state?.trim().toLowerCase().replace(/_/g, '-') || null;
  return normalized || null;
}

const INCOMPLETE_SIGNUP_STATES = new Set<string>([
  COGNITO_CREATED_STATE,
  END_USER_ONBOARDING_STATES.otpVerified,
]);

/**
 * Resolves post-auth navigation from GET /eu/{euId}/state.
 * `cognito_created` / `otp-verified` resume complete-profile; completed users keep expenses.
 * Connection errors (`invalid` / `failed`) stay on end-user home so Retry is shown.
 * First-time `connected` + connect-ready state (no expenses yet) stays on Home dashboard.
 */
export function resolveEndUserJourneyRoute(params: {
  state: EndUserJourneyState | null | undefined;
  accountState?: string | null;
  hasExpenses?: boolean;
}): EndUserJourneyRouteResolution {
  const normalizedState = normalizeEndUserJourneyState(
    typeof params.state === 'string' ? params.state : null
  );
  const canUseFullFlows = canAccessEndUserFullFlows(
    params.accountState,
    params.hasExpenses === true
  );
  const isConnectReady =
    normalizedState != null && END_USER_CONNECT_READY_STATES.has(normalizedState);

  if (normalizedState && INCOMPLETE_SIGNUP_STATES.has(normalizedState)) {
    return { path: userPaths.onboardingCompleteProfile, reason: 'onboarding' };
  }

  // Carrier connection failed — show Home stepper Retry, not find-expenses onboarding.
  if (isEndUserAccountConnectionError(params.accountState)) {
    return { path: userPaths.endUser, reason: 'end-user-home' };
  }

  // Connected but not yet in full flows (no expenses) — Home dashboard stepper.
  if (isConnectReady && isEndUserAccountConnected(params.accountState) && !canUseFullFlows) {
    return { path: userPaths.endUser, reason: 'end-user-home' };
  }

  if (isConnectReady && !canUseFullFlows) {
    return { path: userPaths.onboardingFindExpenses, reason: 'onboarding' };
  }

  if (canUseFullFlows) {
    return { path: userPaths.expenses, reason: 'full-flow' };
  }

  return { path: userPaths.endUser, reason: 'end-user-home' };
}

export function isOnboardingResumePath(path: string): boolean {
  return (
    path === userPaths.onboardingCompleteProfile ||
    path === userPaths.onboardingPersonalDetails ||
    path === userPaths.onboardingFindExpenses ||
    path.startsWith(`${userPaths.onboardingFindExpenses}/`)
  );
}

/** Persist unit id and unlock OnboardingLayout post-OTP routes after login resume. */
export function applyPostAuthNavigationEffects(path: string, unitId: string | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (unitId) {
    window.localStorage.setItem('unitId', unitId);
  }

  if (isOnboardingResumePath(path)) {
    lockOnboardingAfterOtpVerification();
  }
}

export function requiresExpenseCertification(expenseConfirmationConsentAccepted: boolean): boolean {
  return !expenseConfirmationConsentAccepted;
}
